/**
 * WordPress dependencies
 */
import { __ } from "@wordpress/i18n";
import { useEffect, useState, useRef } from "@wordpress/element";
import { ToolbarButton } from "@wordpress/components";
import {
  BlockControls,
  RichText,
  useBlockEditContext
} from "@wordpress/block-editor";
import { displayShortcut } from "@wordpress/keycodes";
import { default as ButtonBase } from "./Button";
import LinkPopover from "./LinkPopover";

const NEW_TAB_REL = "noreferrer noopener";

const ButtonComponent = (props) => {
  const [isSelected, setIsSelected] = useState(false);
  const { isSelected: isBlockSelected } = useBlockEditContext();
  const { onChange } = props;
  const { linkTarget, placeholder, text, url } = props.value || {};

  const richTextRef = useRef();
  const [isEditingURL, setIsEditingURL] = useState(false);
  const isURLSet = !!url;
  const opensInNewTab = linkTarget === "_blank";
  const rel = opensInNewTab ? NEW_TAB_REL : "";

  function unlink() {
    onChange({
      ...props.value,
      url: undefined,
      linkTarget: undefined,
      rel: undefined,
    });
    setIsEditingURL(false);
  }

  useEffect(() => {
    if (!isBlockSelected) {
      setIsEditingURL(false);
      setIsSelected(false);
    }
  }, [isBlockSelected]);

  const onFocus = () => {
    setIsSelected(true);
  };
  // const onBlur = () => {
  //   setIsEditingURL(false);
  // };

  return (
    <>
      <a href={url} onClick={(e) => e.preventDefault()} rel={rel}>
        <ButtonBase
          variant="secondary"
          className={props.className}
          onClick={() => richTextRef.current?.focus()}
          text={
            <RichText
              ref={richTextRef}
              aria-label={__("Button text")}
              placeholder={placeholder || __("Add text…")}
              value={text}
              onChange={(value) => {
                // Remove anchor tags from button text content.
                return onChange({
                  ...props.value,
                  text: value.replace(/<\/?a[^>]*>/g, ""),
                });
              }}
              // withoutInteractiveFormatting
              unstableOnFocus={onFocus}
              // onBlur={onBlur}
              onSplit={props.onSplit}
              onReplace={props.onReplace}
              onMerge={props.mergeBlocks}
              multiline={false}
              inlineToolbar={false}
              tagName="span"
              allowedFormats={props.allowedFormats || []}
              keepPlaceholderOnFocus
            // identifier="text"
            />
          }
        />
      </a>
      {isSelected ? (
        <BlockControls group="block">
          {!isURLSet && (
            <ToolbarButton
              name="link"
              icon={<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
              <path d="M15.6 7.2H14v1.5h1.6c2 0 3.7 1.7 3.7 3.7s-1.7 3.7-3.7 3.7H14v1.5h1.6c2.8 0 5.2-2.3 5.2-5.2 0-2.9-2.3-5.2-5.2-5.2zM4.7 12.4c0-2 1.7-3.7 3.7-3.7H10V7.2H8.4c-2.9 0-5.2 2.3-5.2 5.2 0 2.9 2.3 5.2 5.2 5.2H10v-1.5H8.4c-2 0-3.7-1.7-3.7-3.7zm4.6.9h5.3v-1.5H9.3v1.5z" />
            </svg>}
              title={__("Link")}
              shortcut={displayShortcut.primary("k")}
              onClick={() => setIsEditingURL(true)}
            />
          )}
          {isURLSet && (
            <ToolbarButton
              name="link"
              icon={<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
              <path d="M15.6 7.3h-.7l1.6-3.5-.9-.4-3.9 8.5H9v1.5h2l-1.3 2.8H8.4c-2 0-3.7-1.7-3.7-3.7s1.7-3.7 3.7-3.7H10V7.3H8.4c-2.9 0-5.2 2.3-5.2 5.2 0 2.9 2.3 5.2 5.2 5.2H9l-1.4 3.2.9.4 5.7-12.5h1.4c2 0 3.7 1.7 3.7 3.7s-1.7 3.7-3.7 3.7H14v1.5h1.6c2.9 0 5.2-2.3 5.2-5.2 0-2.9-2.4-5.2-5.2-5.2z" />
            </svg>}
              title={__("Unlink")}
              shortcut={displayShortcut.primaryShift("k")}
              onClick={unlink}
              isActive={true}
            />
          )}
        </BlockControls>
      ) : null}
      {isSelected && (isEditingURL || isURLSet)
        ? (<LinkPopover
          onChange={onChange}
          richTextRef={richTextRef}
          isEditingURL={isEditingURL}
          setIsEditingURL={setIsEditingURL}
          value={props.value}
        />)
        : null}
    </>
  );
};

ButtonComponent.Content = (props) => {
  const { text, url, linkTarget } = props.value || {};

  const button = (
    <ButtonBase
      variant="secondary"
      className={props.className}
      text={<RichText.Content value={text} />}
    />
  );

  if (url) {
    const rel = linkTarget ? NEW_TAB_REL : "";

    return (
      <a href={url} target={linkTarget} rel={rel}>
        {button}
      </a>
    );
  }

  return button;
};

export const Button = ButtonComponent;

export default Button;
